import { PeekyEmitter } from "@shared/peeky.emitter";
import { Peeky } from "./peeky";
import { throttle } from '@shared/throttle';
import { getDimensions, imgload, imgloaded, transitionend } from '@front/dom';


// check_webp_feature:
//   'feature' can be one of 'lossy', 'lossless', 'alpha' or 'animation'.
//   'callback(feature, isSupported)' will be passed back the detection result (in an asynchronous way!)
function check_webp_feature(feature: string, callback) {
  const kTestImages = {
      lossy: "UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA",
      lossless: "UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==",
      alpha: "UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==",
      animation: "UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA"
  };
  const img = new Image();
  img.onload = function () {
      var result = (img.width > 0) && (img.height > 0);
      callback(feature, result);
  };
  img.onerror = function () {
      callback(feature, false);
  };
  img.src = "data:image/webp;base64," + kTestImages[feature];
}
let hasWebp = {
  lossy: false,
  lossless: false,
  alpha: false,
  animation: false
}
for (let key in hasWebp) {
  check_webp_feature(key, (feature: string, result: boolean) => { hasWebp[feature] = result });
}

  
export class PeekyImage extends PeekyEmitter {
  DOM: HTMLImageElement;
  pk: Peeky;
  srcset: any;
  src: string;
  data: any;
  emptySrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///////yH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  throttledResize: (...args: any) => void;
  throttledPreload: (...args: any) => void;;

  private _ready: boolean;

  constructor(image: HTMLImageElement, pk: Peeky) {
    super();

    let JSONdata: any;
    try {
      JSONdata = JSON.parse(image.dataset.image || '{}');
    } catch(e) {
      console.log('Could not parse image data: ');
      console.log(image.dataset.image);
      return;
    }

    this.DOM = image;

    this.pk = pk;
    this.srcset = image.srcset;
    this.src = image.src;
    this.data = JSONdata || {};
    this._ready = false;

    this.throttledResize = throttle(this.onResize).bind(this);
    this.throttledPreload = throttle(this.onPreload).bind(this);

    this.once('imageLoaded', () => {
      this._ready = true;
    });
  
    // Unset src
    image.src = this.emptySrc;
    image.srcset = '';
  }

  onResize() {
    this.load();
  }

  onPreload(e, data) {
    // Needs e.width && e.height
    if (data && data.width && data.height) {
      this.load({
        preload: true,
        dimensions: {
          width: data.width,
          height: data.height
        }
      });
    }
  }

  async focusIn() {
    let image = this.DOM;
    this.onResize();
    image.classList.remove("notInFocus")
    this.pk.on('resize', this.throttledResize);
    this.pk.on('resizeImages', this.throttledResize);
    image.addEventListener('preloadImage', this.throttledPreload);
  }

  async focusOut() {
    let image = this.DOM;
    image.classList.add('notInFocus');
    this.pk.off('resize', this.throttledResize);
    this.pk.off('resizeImages', this.throttledResize);
    image.removeEventListener('preloadImage', this.throttledPreload);
    await transitionend(image);
    image.src = this.emptySrc;
  }

  ready(callback?) {
    if (typeof(callback) === 'function') {
      if (this._ready) {
        callback();
      } else {
        this.on('ready', callback);
      }
    }
    return !!this._ready;
  }

  load(options?) {
    options = options || {};
    let imageUrl = this.getImageUrl(options.dimensions);
    let image = this.DOM;
  
    // Resolve image url to check if current image is the same
    let img = new Image();
    img.src = imageUrl;
    imageUrl = img.src;
    if (image.src === imageUrl) {
      return;
    }
    if (options.preload) {
      return;
    }
  
    let alreadyLoaded = this.ready();  
    if (!alreadyLoaded || !imgloaded(img)) {
      if (!alreadyLoaded) {
        image.classList.add('load');
        image.classList.add('loading');
        image.dispatchEvent(new Event('pk-loading'));
        image.dispatchEvent(new Event('pk-image-loading'));
      }
      imgload(img, async () => {
        image.src = imageUrl;
        if (!alreadyLoaded) {
          image.classList.remove('loading');
          image.dispatchEvent(new Event('pk-loaded'));
          image.dispatchEvent(new Event('pk-image-loaded'));
          await transitionend(image);
          image.classList.remove('load');
        }
      });
    } else {
      image.src = imageUrl;
    }
  
    imgload(image, () => {
      this.triggerAction('imageLoaded', { url: imageUrl });
    });
  }

  revert() {
    let image = this.DOM;
    // Revert to original values
    image.srcset = this.srcset;
    image.src = this.src;
    image.classList.remove('notInFocus');
    image.classList.remove('loading');
    this.pk.off('resize', this.throttledResize);
    this.pk.off('resizeImages', this.throttledResize);
    image.removeEventListener('preloadImage', this.throttledPreload);
  }

  getImageUrl(incomingDimensions) {
    let dpr = Math.max((window.devicePixelRatio || 1) * 0.75, 1); // If hi-res screen, only use 75%
    let dimensions = incomingDimensions || getDimensions(this.DOM);
    let bestWidth = 0;
    let bestHeight = 0;
    let bestUrl: string | undefined;
    let foundMatch = false;
    let containerWidth = dpr * dimensions.width;
    let containerHeight = dpr * dimensions.height;
    let aspectRatio = this.data.width / this.data.height;
    let style = window.getComputedStyle(this.DOM);
    let type = (style.objectFit === 'cover') ? 'cover' : 'contain';
    let versions = this.data.versions || {};
  
    // Loop all versions of image *no*'
    for (let resolution in versions) {
      let thisUrl = versions[resolution];
      let resSplit = resolution.split('x');
      let	thisWidth = parseFloat(resSplit[0]);
      let thisHeight = parseFloat(resSplit[1]);
      let test = false;
  
      if (!thisWidth) {
        thisWidth = thisHeight*aspectRatio;
      }
      if (!thisHeight) {
        thisHeight = thisWidth/aspectRatio;
      }
      
      if (type === 'cover') {
        // This matches the container
        if (thisWidth >= containerWidth && thisHeight >= containerHeight) {
          // This is smaller than previously found match
          if (!foundMatch || thisWidth*thisHeight < bestWidth*bestHeight) {
            foundMatch = true;
            test = true;
          }
        } else {
          // If previously found image also doesn't match, check if this is better
          if ((bestWidth < containerWidth || bestHeight < containerHeight) && (thisWidth*thisHeight > bestWidth*bestHeight)) {
            test = true;
          }
        }
      } else if (type === 'contain') {
        // This matches the container
        if (thisWidth >= containerWidth || thisHeight >= containerHeight) {
          // This is smaller than previously found match
          if (!foundMatch || thisWidth*thisHeight < bestWidth*bestHeight) {
            foundMatch = true;
            test = true;
          }
        } else {
          if (bestWidth < containerWidth && bestHeight < containerWidth && (thisWidth*thisHeight > bestWidth*bestHeight)) {
            test = true;
          }
        }
      }
  
      if (test || !bestUrl) {
        bestWidth = thisWidth;
        bestHeight = thisHeight;
        bestUrl = thisUrl;
      }
    }
  
    if (!bestUrl) {
      bestUrl = this.src;
    }
  
    // Use WebP
    let extension = bestUrl.substr(bestUrl.lastIndexOf('.')+1);
    if (hasWebp.lossy && (extension === 'jpg' || extension === 'jpeg')) {
      bestUrl = bestUrl.replace(/(jpg|jpeg)$/, 'webp');
    }
    else if (hasWebp.alpha && extension === 'png') {
      bestUrl = bestUrl.replace(/png$/, 'webp');
    }
    //else if (hasWebp.animation && extension === 'gif') {
    //  bestUrl = bestUrl.replace(/gif$/, 'webp');
    //}

    // Maybe add CDN URL
    let cdn = pk.property('cdn');
    if (cdn && bestUrl.indexOf('/') === 0) {
      bestUrl = cdn + bestUrl;
    }

    return bestUrl;
  }
}
  